
import img0 from './EnableTCPIP.png';
import img1 from './SQLServerServiceStopStart.png';
import img2 from './SQLServerNewLogin.png';
import img3 from './SQLServerUserMapping.png';
import img4 from './CreateBackupJob.png';
import img5 from './ConfigFileForUpload.png';
  
const content = `# Steps to Encrypt SQL Server Databases

This document details the steps to encrypt a SQL Server database using the OnData Encryption Tool.

Supported SQL Server Versions (with all editions):

- Microsoft SQL Server 2019
- Microsoft SQL Server 2017
- Microsoft SQL Server 2016

## 1. Backup Database

If you do not have a backup of your database already, it is strongly recommended that you backup your database before proceeding with the database encryption. The backup can prevent 
any data loss due to incorrect configuration or other issues during encryption.

## 2. SQL Server Configuration

Before running the data encryption process, you need to configure the database server. The SQL Server needs to be configured with the TCP/IP connectivity. Also, you will need a database user with sufficient permissions to run the OnData backup script (PowerShell) and the OnData Encryption Tool. Below are the details on the configuration.

### 2.1 Enable TCP/IP Connectivity

By default, the OnData backup script (PowerShell) and the OnData Encryption Tool connect to SQL Server over TCP/IP. You can follow the steps below to enable TCP/IP connectivity.

- Launch the **SQL Server Configuration Manager**, and expand the **SQL Server Network Configuration** entry on the left panel;
- Click on **Protocols for &lt;the name of your SQL server instance&gt;**;
- Find the **TCP/IP** protocol in the details panel. If it is **Disabled**, right click on **TCP/IP** and select **Enable**.

![Enable TCP/IP](${ img0 })

- Restart the SQL Server instance. Select the **SQL Server Services** in the left panel. Then in the right panel right click on the SQL Server instance to be restarted and select **Restart**.

![SQL Server Service Stop Start](${ img1 })

### 2.2 Configure Database User with Sufficient Permissions

In order to run the OnData backup script (PowerShell) and the OnData Encryption Tool, you need to configure a database user with appropriate permissions.

1. Minimum user permissions required for the OnData backup script (PowerShell) to perform a schema-only backup of a database:

- db_datareader
- db_ddladmin
- VIEW SERVER STATE

2. Minimum user permissions required for the OnData Encryption Tool to encrypt a database:

- db_datareader
- db_datawriter
- db_ddladmin

You may use an existing database user with the minimum permissions above or create a new database user. To create a new SQL Server database user, you may follow the steps below.

- Open the SQL Server Management Studio (SSMS) and connect to the SQL Server instance with an administrative account;
- Expand **Security** in the left panel and right click on **Logins**, then select **New Login...**;
- Under the **General** tab, enter the desired database username in the **Login name** text box. Select **SQL Server authentication** and enter the desired password in **Password** 
and **Confirm password** textboxes;

![SQL Server New Login](${ img2 })

- Under the **User Mapping** tab, select the name of the database. Then, inside the **Database role membership for** list box, select the **db_datareader**, **db_datawriter** 
and **db_ddladmin** permissions (**Note**: If you are going to create different logins for the OnData backup script and the OnData Encryption Tool, please refer to the above 
minimum permissions required for each login);

![SQL Server User Mapping](${ img3 })

## 3. Create Database at OnData Portal

In order to configure encryption rules for your database, you need to create a database at the OnData Portal and send a backup of your database schema to OnData. The schema-only 
backup is designed to send your database backup to OnData, without sending any of your actual data. 

Please create a backup job for your database according to the following steps:

- Go to **Data Continuity Dashboard**, click on the **Create Backup Job** button;
- Enter the **Database Name**, which must be exactly the same as the name of the database to be encrypted;
- Select **SQLServer** for **Database Engine**;
- Select the appropriate **Engine Version**;
- Select an **Encryption Key**; (**Note**: You can create a new encryption key or select an existing encryption key. An encryption key can be used by multiple databases. In that case, 
the same input value from those databases will produce the same output using the same encryption rule.)
- Click **Create Backup Job** to submit.

![Create Backup Job](${ img4 })

## 4. Run Schema-Only Backup

The database backup script (PowerShell) can be run from the database server, or from a remote machine as long as it can connect to the database remotely.

- Install or upgrade PowerShell to **version 7.1 or higher**. (**Note**: If PowerShell is already installed, you can check your PowerShell version with this command: \`$PSVersionTable\`.)
  - Windows Server: [Installing PowerShell on Windows](https://docs.microsoft.com/en-us/powershell/scripting/install/installing-powershell-core-on-windows?view=powershell-7.1)

  - Linux Server: [Installing PowerShell on Linux](https://docs.microsoft.com/en-us/powershell/scripting/install/installing-powershell-core-on-linux?view=powershell-7.1)

- Download the database backup script (PowerShell) from OnData with the following links:
  - PowerShell Backup Script for Microsoft SQL Server 2019 on [[Windows](download_res#BackupSolution/SQLServer/2019/BackupSQLServer2019-Windows.ps1 'backupmssql')/[Linux](download_res#BackupSolution/SQLServer/2019/BackupSQLServer2019-Linux.ps1 'backupmssql')]

  - PowerShell Backup Script for Microsoft SQL Server 2017 on [[Windows](download_res#BackupSolution/SQLServer/2017/BackupSQLServer2017-Windows.ps1 'backupmssql')/[Linux](download_res#BackupSolution/SQLServer/2017/BackupSQLServer2017-Linux.ps1 'backupmssql')]

  - PowerShell Backup Script for Microsoft SQL Server 2016 on [[Windows](download_res#BackupSolution/SQLServer/2016/BackupSQLServer2016-Windows.ps1 'backupmssql')/[Linux](download_res#BackupSolution/SQLServer/2016/BackupSQLServer2016-Linux.ps1 'backupmssql')]

- Click on the **View Backup Jobs** from the Data Continuity Dashboard;
- Find the backup job created above and then click on the right arrow to view the details;
- Click on the **Config File for Upload** link to download the **BackupConfig.ini** file. It contains configuration information that is unique for handling the backup of this 
database. Move this file to the same folder as the database backup script and make sure its name stays as **BackupConfig.ini**. (**Note**: The operating system may add suffix to the 
file when it is downloaded multiple times. In that case, please delete such suffix from the filename.)

![Config file for Upload](${ img5 })

\`\`\`ini
[Config]
# DatabaseName: The exact name of the database that you want to backup
DatabaseName = AdventureWorks2019

# ServerAddress: The server IP address or Full Qualified Domain Name (FQDN) where the database is hosted
ServerAddress = <Server Address, ex. 127.0.0.1>

# Port: The server port that the database uses for connectivity
Port = 1433

# Username: The name of the database user account who has sufficient permission to backup the database
Username = <Replace with your username>

# Password: The password credential of the database user account
Password = <Replace with your password>

# IntegratedSecurity: When the database type is MS SQL Server, you may choose to use integrated authentication with the
# current Windows account by setting IntegratedSecurity to true. In that case, you do not need to specify the Username or 
# Password. Otherwise please specify the Username and Password, and set IntegratedSecurity to false.
IntegratedSecurity = false

# BackupPath: A local folder that has enough disk space to store the backups temporarily. The backups will be deleted by default after they are encrypted
# and uploaded to OnData. OnData recommends to run the backup from a machine other than the database server. If you need to run the backup directly from 
# the database server, OnData recommends to store the backups in a disk drive that is different from where the SQL Server data files are located. For example,
# '/var/opt/backup' for Linux system, or "C:/Program Files/Microsoft SQL Server/MSSQL15.MSSQLSERVER/MSSQL/Backup" for Windows Server.
BackupPath = <Replace with your local path>

#############################################################################################################
# The following configurations are maintained by OnData. Please do not edit them.
# DatabaseId: Identifies a customer-specific database. DO NOT EDIT
#############################################################################################################
DatabaseId = xx
\`\`\`

- Configure **ServerAddress**, **Username** and **Password** in the **BackupConfig.ini** file; (**Note**: Schema-Only backup does not require you 
to set the **BackupPath**.)
- Execute the command \`./BackupSQLServer2019.ps1 -BackupType s\` to perform a schema-only backup of your database.

## 5. Configure Encryption Rules

Please follow the steps below to configure the encryption rules:

- Go to **Encryption Dashboard**, click on the **Encryption Mappings** button;
- Find the database and edit its encryption mappings.

For more information, please refer to [Encryption Rules](reference_doc#how-to_guides/encryption/encryption_rules).

## 6. Configure OnData Encryption Tool

Below are the configuration files for the OnData Encryption Tool. For more details about the OnData Encryption Tool, please refer to [Encryption Tool](reference_doc#how-to_guides/encryption/encryption_tool).

### 6.1 \`appsettings.ini\`

- \`DatabaseType\`: Indicates the type of database to be encrypted. Please use one of these values:
  - MySQL: 1
  - PostgreSQL: 2
  - SQL Server: 3
  - MariaDB: 5
  - Oracle: 6
  - DB2: 7

- \`ServerAddress\`: The server IP address or Fully Qualified Domain Name (FQDN) where the database is hosted.  

- \`Port\`: The server port that the database uses for connectivity. The default ports are:
  - MySQL: 3306
  - PostgreSQL: 5432
  - SQL Server: 1433
  - MariaDB: 3306
  - Oracle: 1521
  - DB2: 25000

- \`Username\`: The name of the database user account who has sufficient permissions to access the database.

  **Note**: \`Username\` is not required when \`SqlServerIntegratedSecurity\` is set to true.

- \`Password\`: The password of the database user account who has sufficient permissions to access the database. For more information, please see **How to run** section below.
  - If a password is specified on the command line with the "-p {password}" option, use the password from the command line.
  - If such option does not exist in the command line, search the appsettings.ini file for the password.
  - If the password does not exist there either, please enter the password for the database user when prompted.

  **Note**: \`Password\` is not required when \`SqlServerIntegratedSecurity\` is set to true.

- \`SqlServerIntegratedSecurity\`: When database type is SQL Server, indicates whether Username and Password are specified in the connection (when false) or whether the current Windows account credentials are used for authentication (when true).

  **Note**: \`Username\` and \`Password\` are not required when \`SqlServerIntegratedSecurity\` is set to true.

- \`DatabaseName\`: The name of the database to be encrypted. It should match exactly with the database name on your database server and the database name you created at the OnData Portal.


### 6.2 \`ObfuscateConfig.json\`

This is the configuration file for data obfuscation purpose. The data obfuscation rules can be configured at the OnData Portal directly. In case you prefer to 
use this configuration file instead of the OnData Portal, you can update this file with your obfuscation settings. When this configuration file is used, 
the data obfuscation rules configured at the OnData Portal will be ignored. If you do need to use the data obfuscation feature, or you would like to use the data 
obfuscation rules configured at the OnData Portal, please delete this file.  
  
Below is an example of the configuration file:

\`\`\`json
[
  {
    "Schema": "HumanResources",
    "Table": "Employee",
    "Column": "Gender",
    "AllowedValues": [
      "M",
      "F"
    ]
  },
  {
    "Schema": "Production",
    "Table": "Document",
    "Column": "Status",
    "AllowedValues": [
      "1",
      "2",
      "3"
    ]
  }
]
\`\`\`

This configuration file contains an array of columns to be obfuscated. For each column, configure the following four properties:

- \`Schema\`: Schema name.
- \`Table\`: Table name.
- \`Column\`: Column name.
- \`AllowedValues\`: List of allowed values.
  - **Please always use string values here even if the data type of the column is numeric**. The OnData Encryption Tool will convert them to the right data type when the data is imported into the database;
  - AllowedValues should contain at least one value;
  - AllowedValues should not contain duplicated values;
  - Because the configuration file is in JSON format, if the value contains \`&bsol;\` or \`"\`, please replace with \`&bsol;&bsol;\` and \`&bsol;"\`;
  - If the value needs to contain a control character or non-printable character, you may use its Unicode value. For example, if you want the value to contain an LF(line feed), you can use either \`&bsol;n\` or \`&bsol;u000A\`;
  - Do not use the \`N\` prefix of SQL Server.

**Important Notes**:

- \`Schema\`, \`Table\` and \`Column\` are case-insensitive for SQL Server, but are case-sensitive for other database engines;
- If you have an encryption rule configured for a column, do not configure an obfuscation rule for the same column;
- The columns configured in the \`ObfuscateConfig.json\` file must exist in the database;
- Always use string values for the list of \`AllowedValues\`, even if the data type of that column is numeric. The OnData Encryption Tool will convert them to the right data type when the data is imported into the database.

## 7. Run OnData Encryption Tool

Before you run the OnData Encryption Tool, you need to register it as an OnData client. For more information about how to register an OnData client, please refer to the [**OnData Client Registration**](reference_doc#how-to_guides/drivers/client_registration).  

You can run the OnData Encryption Tool from different Shell environments. Below is the list for Windows and Linux.

- Windows
  - Command line
  - PowerShell

- Linux
  - Bash

Command examples:

- Windows: \`.&bsol;OnDataEncryptionTool.exe -p '<password>'\`
- Linux: \`./OnDataEncryptionTool -p '<password>'\`

Note: If you choose to place the password in a pair of single quotation marks, and the password itself contains single quotation marks, you have to replace \`'\` with \`''\` (two single quotation marks).

The password is optional:

- If a password is specified on the command line with the \`-p '<password>'\` option, use the password from the command line.
- If such option does not exist in the command line, search the appsettings.ini file for the password.
- If the password does not exist there either, please enter the password for the database user when prompted.

  **Note**: \`Password\` is not required when \`SqlServerIntegratedSecurity\` is set to true.

## 8. Check Encryption Results

Any failure in data encryption will be logged into the \`encryption-result.json\` file. You may review that file for details after running the OnData Encryption Tool.
`
  
export default content;
